const RECORD_ID_RE = /^[A-Za-z0-9_-]+$/;

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function markdownLink(label, recordId) {
  return `[${label}](/record/${encodeURIComponent(recordId)})`;
}

function isSameOriginAbsoluteUrl(locator) {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return false;
  }
  try {
    return new URL(locator).origin === window.location.origin;
  } catch (_error) {
    return false;
  }
}

function findClosingBracket(source, openIndex) {
  let depth = 0;
  for (let i = openIndex; i < source.length; i += 1) {
    const char = source[i];
    if (char === '\\') {
      i += 1;
      continue;
    }
    if (char === '[') {
      depth += 1;
    } else if (char === ']') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function findClosingParen(source, openIndex) {
  let depth = 0;
  for (let i = openIndex; i < source.length; i += 1) {
    const char = source[i];
    if (char === '\\') {
      i += 1;
      continue;
    }
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function findLineEnd(source, startIndex) {
  const lineEnd = source.indexOf('\n', startIndex);
  return lineEnd === -1 ? source.length : lineEnd;
}

function isLineStart(source, index) {
  return index === 0 || source[index - 1] === '\n';
}

function isHttpAutolinkStart(source, index) {
  return source.startsWith('<http://', index) ||
    source.startsWith('<https://', index);
}

function splitMarkdownLinkSegments(source) {
  const text = typeof source === 'string' ? source : '';
  const segments = [];
  let cursor = 0;

  for (let i = 0; i < text.length; i += 1) {
    if (isHttpAutolinkStart(text, i)) {
      const close = text.indexOf('>', i + 1);
      if (close === -1) continue;
      if (cursor < i) {
        segments.push({ text: text.slice(cursor, i), linkable: true });
      }
      segments.push({
        text: text.slice(i, close + 1),
        linkable: false,
      });
      cursor = close + 1;
      i = close;
      continue;
    }

    const isImageLink = text[i] === '!' && text[i + 1] === '[';
    const isTextLink = text[i] === '[';
    if (!isImageLink && !isTextLink) continue;

    const labelOpen = isImageLink ? i + 1 : i;
    const labelClose = findClosingBracket(text, labelOpen);
    if (labelClose === -1) continue;

    if (!isImageLink && isLineStart(text, i) && text[labelClose + 1] === ':') {
      const lineEnd = findLineEnd(text, labelClose + 2);
      if (cursor < i) {
        segments.push({ text: text.slice(cursor, i), linkable: true });
      }
      segments.push({
        text: text.slice(i, lineEnd),
        linkable: false,
      });
      cursor = lineEnd;
      i = lineEnd - 1;
      continue;
    }

    if (text[labelClose + 1] !== '(') continue;
    const destinationClose = findClosingParen(text, labelClose + 1);
    if (destinationClose === -1) continue;

    if (cursor < i) {
      segments.push({ text: text.slice(cursor, i), linkable: true });
    }
    segments.push({
      text: text.slice(i, destinationClose + 1),
      linkable: false,
    });
    cursor = destinationClose + 1;
    i = destinationClose;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), linkable: true });
  }
  return segments.length ? segments : [{ text, linkable: true }];
}

/**
 * Linkify only backend-resolved Record locators in assistant text.
 * @param {string} text
 * @param {Array} recordCitations
 * @returns {string}
 */
export function linkRecordCitationsInMarkdown(text, recordCitations = []) {
  return splitMarkdownLinkSegments(text)
    .map((segment) => {
      if (!segment.linkable) return segment.text;
      return linkRecordCitationsInText(segment.text, recordCitations);
    })
    .join('');
}

function linkRecordCitationsInText(text, recordCitations = []) {
  let output = typeof text === 'string' ? text : '';
  const citations = Array.isArray(recordCitations) ? recordCitations : [];

  for (const citation of citations) {
    const recordId = citation?.recordId;
    if (!RECORD_ID_RE.test(recordId || '')) continue;
    const escapedId = escapeRegExp(recordId);

    const absoluteRecordUrl = new RegExp(
      `(^|[^A-Za-z0-9_/\\[\\]])` +
        `(https?:\\/\\/[^\\/\\s)>\\]]+\\/record\\/${escapedId}` +
        `(?=$|[\\s)>\\]?#,])(?:[?#][^\\s)>\\],]*)?)`,
      'g'
    );
    output = output.replace(absoluteRecordUrl, (_match, prefix, locator) => {
      if (!isSameOriginAbsoluteUrl(locator)) {
        return `${prefix}${locator}`;
      }
      return `${prefix}${markdownLink(locator, recordId)}`;
    });

    const relativeRecordUrl = new RegExp(
      `(^|[^A-Za-z0-9_/\\[\\]])` +
        `(\\/record\\/${escapedId}` +
        `(?=$|[\\s)>\\]?#,])(?:[?#][^\\s)>\\],]*)?)`,
      'g'
    );
    output = output.replace(relativeRecordUrl, (_match, prefix, locator) =>
      `${prefix}${markdownLink(locator, recordId)}`
    );

    const recordScheme = new RegExp(
      `(^|[^A-Za-z0-9_/\\[\\]])` +
        `(record:${escapedId})(?![A-Za-z0-9_/-])`,
      'g'
    );
    output = output.replace(recordScheme, (_match, prefix, locator) =>
      `${prefix}${markdownLink(locator, recordId)}`
    );
  }

  return output;
}
